import { ParticipantInfo, SummaryBlockItem } from '@api';
import { getDateString, useClassNames } from '@common';
import { Button, Checkbox, DayPickerInput, IconButton, ParticipantSelect, SummaryBlockItemWitCoord, Textarea } from '@components';
import { PencilIcon } from '@icons';
import React, { useCallback } from 'react';
import { useState } from 'react';
import { TaskLinkComp } from '../task-link';
import './Tasks.scss';

interface TaskItemProps {
	task: SummaryBlockItemWitCoord,
	onEdit: (newItem: SummaryBlockItemWitCoord) => void,
	canEditResume: boolean,
	allowSelection?: boolean,
	isSelected?: boolean,
	onChangeSelection?: (changedItemId: string) => void,
}

function TaskItem(props: TaskItemProps): JSX.Element {
	const {
		task,
		onEdit,
		canEditResume,
		allowSelection,
		isSelected,
		onChangeSelection,
	} = props;

    const [editMode, setEditMode] = useState<boolean>(false);
	const [editedTask, setEditedTask] = useState<SummaryBlockItem>(task.item);

	const onChangeText = useCallback((newText: string) => {
		const newTask = new SummaryBlockItem(editedTask);
		newTask.text = newText;
		setEditedTask(newTask);
	}, [editedTask, setEditedTask]);

	const onChangeExecutor = useCallback((newExecutors: ParticipantInfo[]) => {
		const newTask = new SummaryBlockItem(editedTask);
		newTask.selectedPersons = newExecutors;
		setEditedTask(newTask);
	}, [editedTask, setEditedTask]);

	const onChangeDate = useCallback((newDate: Date) => {
		const newTask = new SummaryBlockItem(editedTask);
		newTask.selectedDate = newDate;
		setEditedTask(newTask);
	}, [editedTask, setEditedTask]);

    const textView = editMode 
        ? <Textarea autoFocus={true} value={editedTask.text} onValueChange={onChangeText} rows={3} />
        : <>{editedTask.text}</>

	let executorsView = null;
	let dateView = null;

	if (editMode) {
		executorsView = <ParticipantSelect 
			suggestedValues={[]} 
			values={editedTask.selectedPersons || []} 
			onChange={onChangeExecutor} 
			maxValues={1}
		/>;

		dateView = <DayPickerInput 
			value={editedTask.selectedDate} 
			onDayChange={onChangeDate}
		/>
	} else {
		const executors = editedTask.selectedPersons && editedTask.selectedPersons.length > 0 && editedTask.selectedPersons
			.map(p => <span className="person">{p.name}</span>)
			.reduce((acc, p) => acc == null ? p : <>{acc}, {p}</>);

		executorsView = <><span className='label'>Исполнитель: </span>{executors ? executors : "нет исполнителя"}</>;

		const dates = editedTask.selectedDate && getDateString(editedTask.selectedDate);
		dateView = <><span className='label'>Срок: </span>{dates ? dates : "не установлен"}</>;
	}	

    const classes = useClassNames(
        'task-item',
        editMode && 'edit',
    );

    const trailClasses = useClassNames(
        'task-item__trail',
        !editMode && 'hidden',
    );

	const onEditClick = useCallback(() => { 
		setTimeout(() => {
			setEditMode(true);
		});
	}, [setEditMode]);

	const onCancelClick = useCallback(() => { 
		setTimeout(() => {
			setEditedTask(task.item);
			setEditMode(false);
		});
	}, [setEditMode, task]);

	const onSubmitClick = useCallback(() => { 
		setTimeout(() => {
			setEditMode(false);
			editedTask.isAuto = false;
			const result: SummaryBlockItemWitCoord = {...task, ...{
				item: editedTask,
			} as SummaryBlockItemWitCoord};
			
			onEdit(result);
		});
	}, [setEditMode, onEdit, editedTask, task]);

	const canEdit = canEditResume && !task.item.taskLink;	

	return <div className={classes}>
		{allowSelection && 
		<div className='task-item__check'>
			<Checkbox
				boolValue={isSelected}
				onValueChange={() => onChangeSelection(task.item.id)}
			/>
		</div>}
		<div className='task-item__main'>
			<div className='task-item__text'>
				{textView}
			</div>
			<div className='task-item__details'>
				<div className='task-item__executor'>
					{executorsView}
				</div>
				<div className='task-item__date'>
					{dateView}
				</div>
			</div>
			<div>
				{task && editedTask.taskLink && <TaskLinkComp className='task-item__link' taskLink={editedTask.taskLink} />}
			</div>
			{editMode && <div className='task-item__buttons'>
				<Button theme="primary" onClick={onSubmitClick}>Сохранить</Button>
				<Button onClick={onCancelClick}>Отменить</Button>
			</div>}
		</div>
        {canEdit && !editMode && <div className={trailClasses}>
            <IconButton
                onClick={onEditClick}
                icon={<PencilIcon />}
                iconSize="18px"
                size="nogap"
            />
        </div>}
	</div>
}

// const MemoisedTaskItem = React.memo(TaskItem);
// export {MemoisedTaskItem as TaskItem};
export {TaskItem};